import { ToolItem } from '../types';

export interface CalculatorToolItem extends ToolItem {
  calcCategory: 'finance' | 'health' | 'math-units' | 'time-daily';
  calcCategoryLabel: string;
  iconBgColor: string;
  iconTextColor: string;
  iconName: string;
}

export const CALCULATOR_CATEGORIES_TABS = [
  { id: 'all', label: 'All Calculators', icon: 'Calculator' },
  { id: 'finance', label: 'Finance & Money', icon: 'DollarSign' },
  { id: 'health', label: 'Health & Fitness', icon: 'Heart' },
  { id: 'math-units', label: 'Math & Conversions', icon: 'Percent' },
  { id: 'time-daily', label: 'Time & Utilities', icon: 'Clock' }
];

export const CALCULATOR_TOOLS_DATA: CalculatorToolItem[] = [
  // --- FINANCE & MONEY (22 Tools) ---
  {
    id: 'loan-calculator',
    name: 'Loan Calculator',
    description: 'Calculate monthly loan repayments, total interest accrued, and complete amortization schedules.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'DollarSign',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'emi-calculator',
    name: 'EMI Calculator',
    description: 'Determine Equated Monthly Installments for home, auto, or personal loans.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'CreditCard',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'interest-calculator',
    name: 'Interest Calculator',
    description: 'Calculate total payout and earned interest over time for fixed deposits or savings.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-green-500/15',
    iconTextColor: 'text-green-500',
    iconName: 'TrendingUp',
    actionType: 'generic'
  },
  {
    id: 'gst-calculator',
    name: 'GST Calculator',
    description: 'Calculate inclusive or exclusive Goods and Services Tax amounts effortlessly.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Receipt',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'vat-calculator',
    name: 'VAT Calculator',
    description: 'Add or subtract Value Added Tax (VAT) percentages from gross or net prices.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'FileCheck',
    actionType: 'generic'
  },
  {
    id: 'discount-calculator',
    name: 'Discount Calculator',
    description: 'Find final sale price and total saved money after promotional discount percentages.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Tag',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'percentage-calculator',
    name: 'Percentage Calculator',
    description: 'Calculate relative percentages, percentage increases, decreases, and fractions.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Percent',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'tip-calculator',
    name: 'Tip Calculator',
    description: 'Calculate gratuity tips quickly based on service quality and split the total bill.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Coins',
    actionType: 'generic'
  },
  {
    id: 'split-bill',
    name: 'Split Bill Calculator',
    description: 'Divide restaurant tabs, group travel expenses, and shared bills evenly among friends.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'Users',
    actionType: 'generic'
  },
  {
    id: 'business-profit',
    name: 'Business Profit Calculator',
    description: 'Calculate net profit margin, total revenue, and operating expense ratios.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Briefcase',
    actionType: 'generic'
  },
  {
    id: 'break-even',
    name: 'Break-even Calculator',
    description: 'Determine sales volume required to cover fixed costs and reach 0 net loss.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Scale',
    actionType: 'generic'
  },
  {
    id: 'roi-calculator',
    name: 'ROI Calculator',
    description: 'Calculate Return on Investment percentage and total gain ratio on capital investments.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'PieChart',
    actionType: 'generic'
  },
  {
    id: 'margin-calculator',
    name: 'Margin Calculator',
    description: 'Calculate gross profit margin percentage based on item cost and retail selling price.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'BarChart',
    actionType: 'generic'
  },
  {
    id: 'markup-calculator',
    name: 'Markup Calculator',
    description: 'Determine required selling price and markup percentage to meet gross revenue targets.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-lime-500/15',
    iconTextColor: 'text-lime-500',
    iconName: 'ArrowUpRight',
    actionType: 'generic'
  },
  {
    id: 'tax-calculator',
    name: 'Tax Calculator',
    description: 'Estimate income tax brackets, deductions, and expected refund payouts.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-red-500/15',
    iconTextColor: 'text-red-500',
    iconName: 'Building2',
    actionType: 'generic'
  },
  {
    id: 'electricity-bill',
    name: 'Electricity Bill Calculator',
    description: 'Estimate monthly home energy bills by appliance wattage usage and kilowatt-hour rates.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-yellow-500/15',
    iconTextColor: 'text-yellow-500',
    iconName: 'Zap',
    actionType: 'generic'
  },
  {
    id: 'compound-interest',
    name: 'Compound Interest Calculator',
    description: 'Visualize compounding growth over years with daily, monthly, or annual interest cycles.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'LineChart',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'simple-interest',
    name: 'Simple Interest Calculator',
    description: 'Calculate basic principal interest accumulation without compounding cycles.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Calculator',
    actionType: 'generic'
  },
  {
    id: 'retirement-calculator',
    name: 'Retirement Calculator',
    description: 'Estimate nest egg savings needed for retirement lifestyle and inflation projections.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Shield',
    actionType: 'generic'
  },
  {
    id: 'salary-calculator',
    name: 'Salary Calculator',
    description: 'Convert hourly wages to annual salary, take-home net pay, and bi-weekly paychecks.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Banknote',
    actionType: 'generic'
  },
  {
    id: 'overtime-calculator',
    name: 'Overtime Calculator',
    description: 'Calculate time-and-a-half or double-time overtime wage pay rates.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Clock',
    actionType: 'generic'
  },
  {
    id: 'savings-goal',
    name: 'Savings Goal Calculator',
    description: 'Calculate required monthly savings deposits to reach your target savings milestone.',
    calcCategory: 'finance',
    calcCategoryLabel: 'Finance & Money',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'PiggyBank',
    actionType: 'generic'
  },

  // --- HEALTH & FITNESS (14 Tools) ---
  {
    id: 'age-calculator',
    name: 'Age Calculator',
    description: 'Calculate exact age in years, months, days, hours, and minutes from birthdate.',
    calcCategory: 'health',
    calcCategoryLabel: 'Health & Fitness',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'Cake',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'bmi-calculator',
    name: 'BMI Calculator',
    description: 'Check Body Mass Index score and compare health status against WHO standard ranges.',
    calcCategory: 'health',
    calcCategoryLabel: 'Health & Fitness',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Activity',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'bmr-calculator',
    name: 'BMR Calculator',
    description: 'Calculate Basal Metabolic Rate calories burned at rest using Harris-Benedict formula.',
    calcCategory: 'health',
    calcCategoryLabel: 'Health & Fitness',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Flame',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'body-fat-calculator',
    name: 'Body Fat Calculator',
    description: 'Estimate body fat percentage using US Navy circumference tape measurements.',
    calcCategory: 'health',
    calcCategoryLabel: 'Health & Fitness',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'User',
    actionType: 'generic'
  },
  {
    id: 'ideal-weight',
    name: 'Ideal Weight Calculator',
    description: 'Find healthy body weight ranges based on height, gender, and frame size equations.',
    calcCategory: 'health',
    calcCategoryLabel: 'Health & Fitness',
    iconBgColor: 'bg-yellow-500/15',
    iconTextColor: 'text-yellow-500',
    iconName: 'Scale',
    actionType: 'generic'
  },
  {
    id: 'pregnancy-calculator',
    name: 'Pregnancy Calculator',
    description: 'Estimate due date, current pregnancy week, and trimester milestones.',
    calcCategory: 'health',
    calcCategoryLabel: 'Health & Fitness',
    iconBgColor: 'bg-fuchsia-500/15',
    iconTextColor: 'text-fuchsia-500',
    iconName: 'Heart',
    actionType: 'generic'
  },
  {
    id: 'calorie-calculator',
    name: 'Calorie Calculator',
    description: 'Calculate daily calorie needs for weight loss, maintenance, or muscle gain goals.',
    calcCategory: 'health',
    calcCategoryLabel: 'Health & Fitness',
    iconBgColor: 'bg-red-500/15',
    iconTextColor: 'text-red-500',
    iconName: 'Apple',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'water-intake-calculator',
    name: 'Water Intake Calculator',
    description: 'Determine daily hydration needs based on weight, climate, and exercise activity level.',
    calcCategory: 'health',
    calcCategoryLabel: 'Health & Fitness',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Droplet',
    actionType: 'generic'
  },
  {
    id: 'bmi-chart',
    name: 'BMI Chart',
    description: 'Interactive WHO Body Mass Index classification chart across height and weight axes.',
    calcCategory: 'health',
    calcCategoryLabel: 'Health & Fitness',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'BarChart2',
    actionType: 'generic'
  },
  {
    id: 'sleep-calculator',
    name: 'Sleep Calculator',
    description: 'Calculate optimal wake up times based on 90-minute REM sleep cycles.',
    calcCategory: 'health',
    calcCategoryLabel: 'Health & Fitness',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Moon',
    actionType: 'generic'
  },
  {
    id: 'ovulation-calculator',
    name: 'Ovulation Calculator',
    description: 'Estimate fertile window and next ovulation date based on menstrual cycle lengths.',
    calcCategory: 'health',
    calcCategoryLabel: 'Health & Fitness',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'CalendarHeart',
    actionType: 'generic'
  },
  {
    id: 'macros-calculator',
    name: 'Macros Calculator',
    description: 'Calculate daily protein, carbohydrate, and fat gram targets for fitness nutrition.',
    calcCategory: 'health',
    calcCategoryLabel: 'Health & Fitness',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Dumbbell',
    actionType: 'generic'
  },
  {
    id: 'one-rep-max',
    name: 'One Rep Max Calculator',
    description: 'Estimate 1RM weightlifting maximum using Epley and Brzycki strength formulas.',
    calcCategory: 'health',
    calcCategoryLabel: 'Health & Fitness',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Zap',
    actionType: 'generic'
  },
  {
    id: 'heart-rate-zones',
    name: 'Heart Rate Zones',
    description: 'Calculate target aerobic, fat burn, and anaerobic cardiovascular heart rate training zones.',
    calcCategory: 'health',
    calcCategoryLabel: 'Health & Fitness',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'HeartPulse',
    actionType: 'generic'
  },

  // --- MATH & CONVERSIONS (10 Tools) ---
  {
    id: 'scientific-calculator',
    name: 'Scientific Calculator',
    description: 'Full-featured scientific calculator supporting trigonometry, logarithms, powers, and roots.',
    calcCategory: 'math-units',
    calcCategoryLabel: 'Math & Conversions',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Calculator',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'unit-converter',
    name: 'Unit Converter',
    description: 'All-in-one unit converter for metric and imperial measurement system conversions.',
    calcCategory: 'math-units',
    calcCategoryLabel: 'Math & Conversions',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'ArrowLeftRight',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'currency-calculator',
    name: 'Currency Calculator',
    description: 'Convert values between global currencies with customizable exchange rate presets.',
    calcCategory: 'math-units',
    calcCategoryLabel: 'Math & Conversions',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Coins',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'length-converter',
    name: 'Length Converter',
    description: 'Convert meters, kilometers, feet, inches, miles, and nautical yards.',
    calcCategory: 'math-units',
    calcCategoryLabel: 'Math & Conversions',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Ruler',
    actionType: 'generic'
  },
  {
    id: 'weight-converter',
    name: 'Weight Converter',
    description: 'Convert kilograms, grams, pounds, ounces, metric tons, and stone measurements.',
    calcCategory: 'math-units',
    calcCategoryLabel: 'Math & Conversions',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Weight',
    actionType: 'generic'
  },
  {
    id: 'speed-converter',
    name: 'Speed Converter',
    description: 'Convert km/h, mph, knots, and meters per second velocity values.',
    calcCategory: 'math-units',
    calcCategoryLabel: 'Math & Conversions',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Gauge',
    actionType: 'generic'
  },
  {
    id: 'temperature-converter',
    name: 'Temperature Converter',
    description: 'Convert Celsius, Fahrenheit, and Kelvin scale temperatures instantly.',
    calcCategory: 'math-units',
    calcCategoryLabel: 'Math & Conversions',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Thermometer',
    actionType: 'generic'
  },
  {
    id: 'area-converter',
    name: 'Area Converter',
    description: 'Convert square meters, square feet, acres, hectares, and square kilometers.',
    calcCategory: 'math-units',
    calcCategoryLabel: 'Math & Conversions',
    iconBgColor: 'bg-lime-500/15',
    iconTextColor: 'text-lime-500',
    iconName: 'Maximize',
    actionType: 'generic'
  },
  {
    id: 'volume-converter',
    name: 'Volume Converter',
    description: 'Convert liters, milliliters, US gallons, fluid ounces, and cubic meters.',
    calcCategory: 'math-units',
    calcCategoryLabel: 'Math & Conversions',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Box',
    actionType: 'generic'
  },
  {
    id: 'fuel-cost',
    name: 'Fuel Cost Calculator',
    description: 'Calculate trip fuel cost and gas consumption based on distance and mileage MPGs.',
    calcCategory: 'math-units',
    calcCategoryLabel: 'Math & Conversions',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Fuel',
    actionType: 'generic'
  },

  // --- TIME, DATES & UTILITIES (6 Tools) ---
  {
    id: 'time-difference',
    name: 'Time Difference',
    description: 'Calculate exact elapsed time hours and minutes between two timestamps.',
    calcCategory: 'time-daily',
    calcCategoryLabel: 'Time & Utilities',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Clock',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'date-difference',
    name: 'Date Difference',
    description: 'Calculate days, weeks, and business calendar days between two calendar dates.',
    calcCategory: 'time-daily',
    calcCategoryLabel: 'Time & Utilities',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Calendar',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'time-zone-calculator',
    name: 'Time Zone Calculator',
    description: 'Convert meeting times across global UTC and GMT time zone regions.',
    calcCategory: 'time-daily',
    calcCategoryLabel: 'Time & Utilities',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Globe',
    actionType: 'generic'
  },
  {
    id: 'countdown',
    name: 'Countdown Timer',
    description: 'Set custom live countdown timers for events, launches, and deadlines.',
    calcCategory: 'time-daily',
    calcCategoryLabel: 'Time & Utilities',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Timer',
    actionType: 'generic'
  },
  {
    id: 'random-number',
    name: 'Random Number Generator',
    description: 'Generate cryptographically uniform random numbers within custom ranges.',
    calcCategory: 'time-daily',
    calcCategoryLabel: 'Time & Utilities',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Hash',
    actionType: 'generic'
  },
  {
    id: 'dice-roller',
    name: 'Dice Roller',
    description: 'Virtual multi-sided dice rolling simulator (d4, d6, d8, d10, d12, d20, d100).',
    calcCategory: 'time-daily',
    calcCategoryLabel: 'Time & Utilities',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Dices',
    actionType: 'generic'
  }
];
